import {
  IAccountRespSchema,
  IPayment,
  PAYMENT_STATUS_DISPLAY,
  PaymentMethodTypeEnum,
  PaymentStatusEnum,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MText } from '../../../components/Monetize';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  currencyBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  idBodyTemplate,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { PaymentMethodTypeEnumDisplay } from '../../../constants/paymentMethods';
import { logger } from '../../../services/logger';

const columnHelper = createColumnHelper<IPayment>();

export const PaymentListStatusTagStyle: {
  [key in PaymentStatusEnum]: {
    color: string;
    bgColor?: string;
    px?: number;
    borderRadius?: number;
    fontStyle?: string;
  };
} = {
  FAILED: {
    color: 'tRed.base',
    bgColor: 'tRed.cosmos',
    px: 2,
    borderRadius: 4,
  },
  PROCESSING: {
    color: 'tGray.darkGrayPurple',
    bgColor: 'transparent',
  },
  SUCCESS: {
    color: 'tGray.darkGrayPurple',
    bgColor: 'transparent',
  },
  PENDING: {
    color: 'tGray.darkGrayPurple',
    bgColor: 'transparent',
  },
  CANCELED: {
    color: 'tGray.darkGrayPurple',
    bgColor: 'transparent',
  },
  REFUNDED: {
    color: 'tGray.darkGrayPurple',
    bgColor: 'transparent',
  },
  PARTIAL_REFUND: {
    color: 'tGray.darkGrayPurple',
    bgColor: 'transparent',
  },
  SUCCEEDED: {
    color: 'tGray.darkGrayPurple',
    bgColor: 'transparent',
  },
};

export interface PaymentListColumnContext extends ColumnContextBase<IPayment> {
  account: IAccountRespSchema;
}

/** tableId: external (hook-managed filters) */
export const PAYMENT_LIST_CATALOG: ColumnCatalog<
  IPayment,
  PaymentListColumnContext
> = [
  {
    id: 'id',
    label: 'ID',
    customizable: false,
    build: () =>
      columnHelper.accessor('id', {
        ...getColHeaderWithSize('ID', 200),
        cell: idBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'paymentDate',
    label: 'Payment Date',
    build: () =>
      columnHelper.accessor('paymentDate', {
        ...getColHeaderWithSize('Payment Date', 160),
        ...getColHeaderFilters('DATE'),
        cell: (info) => {
          const payment = info.row.original;
          try {
            return (
              <MText lineClamp={1}>
                {payment.paymentType === PaymentMethodTypeEnum.MANUAL
                  ? toDateShort(payment.paymentDate)
                  : toDateShort(payment.createDate)}
              </MText>
            );
          } catch (ex) {
            logger.warn('Could not generate template', {
              payment,
              property: 'paymentDate',
              ex,
            });
            return null;
          }
        },
        enableSorting: true,
      }),
  },
  {
    id: 'description',
    label: 'Description',
    build: () =>
      columnHelper.accessor('description', {
        ...getColHeaderWithSize('Description', 250),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'paymentStatus',
    label: 'Status',
    build: () =>
      columnHelper.accessor('paymentStatus', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            PaymentStatusEnum,
            PAYMENT_STATUS_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: PAYMENT_STATUS_DISPLAY,
          styleMap: PaymentListStatusTagStyle,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'paymentType',
    label: 'Type',
    build: () =>
      columnHelper.accessor('paymentType', {
        ...getColHeaderWithSize('Type', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            PaymentMethodTypeEnum,
            PaymentMethodTypeEnumDisplay,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: PaymentMethodTypeEnumDisplay,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'amount',
    label: 'Amount',
    build: ({ account }) =>
      columnHelper.accessor('amount', {
        ...getColHeaderWithSize('Amount', 150),
        cell: currencyBodyTemplate({
          currencyProperty: 'currency',
          currencyFallback: account?.defaultCurrency,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'unAppliedAmount',
    label: 'Remaining',
    build: ({ account }) =>
      columnHelper.accessor('unAppliedAmount', {
        ...getColHeaderWithSize('Remaining', 150),
        cell: currencyBodyTemplate({
          currencyProperty: 'currency',
          currencyFallback: account?.defaultCurrency,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        // TODO: BE needs to support these!
        // ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        // TODO: BE needs to support these!
        // ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
      }),
  },
  actionCatalogColumn<IPayment>(),
];
